import {
  SET_FOLDERS,
  RESET_FOLDERS_STATE,
  ADD_FOLDER,
  ADD_FOLDER_ITEMS,
  RENAME_FOLDER,
  REMOVE_FOLDER,
  SELECT_CARD,
  UNSELECT_CARD,
  RESET_CARDS_STATE,
  SET_RECENT_FOLDERS,
} from "./types";
import {
  ADD_TO_FOLDER_ENDPOINT,
  CREATE_FOLDER_ENDPOINT,
  FOLDERS_ENDPOINT,
  FOLDER_CONTENTS_ENDPOINT,
  FOLDER_OPERATIONS_ENDPOINT,
  REMOVE_FROM_FOLDER_ENDPOINT,
} from "./endpoints";
import axios from "axios";
import * as errorHandlerActions from "./errorHandlerActions";
import Format from "string-format";
import { t } from "i18next";
import { showToast } from "../_utils/showToast";
import { analyticsTrackEvent } from "../_utils/common";

export const setFolders = (payload) => (dispatch) => {
  dispatch({
    type: SET_FOLDERS,
    payload: payload,
  });
};

export const setRecentFolders = (payload) => (dispatch) => {
  dispatch({
    type: SET_RECENT_FOLDERS,
    payload: payload,
  });
};

export const fetchFolders = (payload, signalToken) => (dispatch) => {
  const { searchTerm, props, updateRecent } = payload;
  const page = props.foldersPage ? props.foldersPage : 1;

  let endpoint = new URL(FOLDERS_ENDPOINT);
  endpoint.searchParams.append("page", page);

  if (searchTerm.length > 0) {
    endpoint.searchParams.append("search", `name:${searchTerm}`);
  }

  return new Promise((resolve, reject) => {
    axios
      .get(endpoint.href, {
        cancelToken: signalToken,
      })
      .then((res) => {
        resolve(res);
        dispatch(
          setFolders({
            data: res.data.results,
            hasMore: res.data.next !== null,
          })
        );

        if (updateRecent) {
          dispatch(setRecentFolders(res.data.results.slice(0, 10)));
        }
      })
      .catch((error) => {
        reject(error);
        dispatch(errorHandlerActions.handleHTTPError(error));
      });
  });
};

export const selectCard = (payload) => (dispatch) => {
  dispatch({
    type: SELECT_CARD,
    payload: payload,
  });
};

export const unSelectCard = (payload) => (dispatch) => {
  dispatch({
    type: UNSELECT_CARD,
    payload: payload,
  });
};

export const resetCardsSelection = () => (dispatch) => {
  dispatch({
    type: RESET_CARDS_STATE,
  });
};

export const addFolder = (payload) => (dispatch) => {
  dispatch({
    type: ADD_FOLDER,
    payload: payload,
  });
};

export const addFolderItems = (payload) => (dispatch) => {
  dispatch({
    type: ADD_FOLDER_ITEMS,
    payload: payload,
  });
};

export const renameFolder = (payload) => (dispatch) => {
  dispatch({
    type: RENAME_FOLDER,
    payload: payload,
  });
};

export const removeFolder = (payload) => (dispatch) => {
  dispatch({
    type: REMOVE_FOLDER,
    payload: payload,
  });
};

export const resetFolders = () => (dispatch) => {
  dispatch({
    type: RESET_FOLDERS_STATE,
  });
};

export const createNewFolder = (name, signalToken, props) => (dispatch) => {
  const payload = {
    name: name,
  };

  return new Promise((resolve, reject) => {
    axios
      .post(CREATE_FOLDER_ENDPOINT, payload, {
        cancelToken: signalToken,
      })
      .then((res) => {
        const payload = {
          searchTerm: "",
          props: props,
          updateRecent: true,
        };
        dispatch(fetchFolders(payload, signalToken));

        showToast({
          description: t("created.success", {
            folder_name: name,
            ns: "folders",
          }),
          title: "Success",
          status: "success",
        });

        analyticsTrackEvent("folderCreated", {
          folder: res.data,
        });
        resolve(res.data);
      })
      .catch((error) => {
        reject(error);
        showToast({
          description: t("created.failed", { ns: "folders" }),
          title: "Error",
          status: "error",
        });
      });
  });
};

export const updateFolderName =
  (folderId, payload, signalToken) => (dispatch) => {
    return new Promise((resolve, reject) => {
      axios
        .put(Format(FOLDER_OPERATIONS_ENDPOINT, folderId), payload, {
          cancelToken: signalToken,
        })
        .then((res) => {
          resolve(res.data);
          dispatch(renameFolder(res.data));

          showToast({
            description: t("rename.success", { ns: "folders" }),
            title: "Success",
            status: "success",
          });

          analyticsTrackEvent("folderRenamed");
        })
        .catch((error) => {
          reject(error);
          showToast({
            description: t("rename.failed", { ns: "folders" }),
            title: "Error",
            status: "error",
          });
        });
    });
  };

export const deleteFolder = (folder, signalToken, props) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .delete(Format(FOLDER_OPERATIONS_ENDPOINT, folder.id), {
        cancelToken: signalToken,
      })
      .then((res) => {
        resolve(res.data);
        dispatch(resetFolders());
        const payload = {
          searchTerm: "",
          props: props,
          updateRecent: true,
        };
        dispatch(fetchFolders(payload, signalToken));

        showToast({
          description: t("delete.success", {
            folder_name: folder.name,
            ns: "folders",
          }),
          title: "Success",
          status: "success",
        });

        analyticsTrackEvent("folderDeleted", {
          folder: folder,
        });
      })
      .catch((error) => {
        reject(error);
        showToast({
          description: t("delete.failed"),
          title: "Error",
          status: "error",
        });
      });
  });
};

export const moveToFolder =
  (payload, folderId, folderName, signalToken) => (dispatch) => {
    return new Promise((resolve, reject) => {
      axios
        .post(Format(ADD_TO_FOLDER_ENDPOINT, folderId), payload, {
          cancelToken: signalToken,
        })
        .then((res) => {
          resolve(res.data);
          showToast({
            description: t("move_to_folder.success", {
              ns: "folders",
              folder_name: folderName,
            }),
            title: "Success",
            status: "success",
          });

          analyticsTrackEvent("movedToFolder");
        })
        .catch((error) => {
          reject(error);
          showToast({
            description: t("move_to_folder.failed", { ns: "folders" }),
            title: "Error",
            status: "error",
          });
        });
    });
  };

export const fetchFolderContent =
  (folderId, folderOrg, page = 1, signalToken) =>
  (dispatch) => {
    return new Promise((resolve, reject) => {
      axios
        .get(Format(FOLDER_CONTENTS_ENDPOINT, folderId, folderOrg, page), {
          cancelToken: signalToken,
        })
        .then((res) => {
          resolve(res.data);
        })
        .catch((error) => {
          reject(error);
          dispatch(errorHandlerActions.handleHTTPError(error));
        });
    });
  };

export const removeFolderContent =
  (folderId, payload, signalToken) => (dispatch) => {
    return new Promise((resolve, reject) => {
      axios
        .post(Format(REMOVE_FROM_FOLDER_ENDPOINT, folderId), payload, {
          cancelToken: signalToken,
        })
        .then((res) => {
          resolve(res.data);
          showToast({
            description: t("remove_from_folder.success", {
              ns: "folders",
            }),
            title: "Success",
            status: "success",
          });

          analyticsTrackEvent("removedFromFolder");
        })
        .catch((error) => {
          reject(error);
          showToast({
            description: t("remove_from_folder.failed", {
              ns: "folders",
            }),
            title: "Error",
            status: "error",
          });
        });
    });
  };
